/**
 * Bath Category Query and Mutation Hooks
 *
 * React Query hooks for bath category CRUD operations.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ritualBathQueryKeys,
  ritualBathRepository,
} from "../repositories/ritualBath.repository";
import type {
  BathCategory,
  CreateBathCategoryPayload,
  UpdateBathCategoryPayload,
} from "../types/ritualBath.types";

// ============================================================================
// Query Key Helpers
// ============================================================================

export const bathCategoryQueryKey = (terreiroId?: string) =>
  ritualBathQueryKeys.categories.list(terreiroId);
export const bathCategoryByIdQueryKey = (id: string) =>
  ritualBathQueryKeys.categories.detail(id);

// ============================================================================
// Mutation Key Helpers
// ============================================================================

export const bathCategoryMutationKeys = {
  create: ["bath-categories", "create"] as const,
  update: (id?: string) => ["bath-categories", "update", id] as const,
  delete: (id?: string) => ["bath-categories", "delete", id] as const,
};

// ============================================================================
// List Bath Categories Query
// ============================================================================

/**
 * Hook to fetch system bath categories, plus the terreiro's own when `terreiroId` is given
 */
export function useBathCategoriesQuery(enabled = true, terreiroId?: string) {
  return useQuery<BathCategory[], Error>({
    queryKey: bathCategoryQueryKey(terreiroId),
    queryFn: () => ritualBathRepository.listBathCategories(terreiroId),
    enabled,
    staleTime: 1000 * 60 * 10, // 10 minutes (categories change less frequently)
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
}

// ============================================================================
// Get Bath Category By ID Query
// ============================================================================

interface UseBathCategoryByIdQueryProps {
  id?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch a single bath category by ID
 */
export function useBathCategoryByIdQuery({
  id,
  enabled = true,
}: UseBathCategoryByIdQueryProps) {
  return useQuery<BathCategory, Error>({
    queryKey: bathCategoryByIdQueryKey(id || ""),
    queryFn: () => {
      if (!id) throw new Error("Category ID is required");
      return ritualBathRepository.getBathCategoryById(id);
    },
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 10,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
}

// ============================================================================
// Create Bath Category Mutation
// ============================================================================

/**
 * Mutation hook to create a new bath category
 */
export function useCreateBathCategoryMutation() {
  const qc = useQueryClient();

  return useMutation<BathCategory, Error, CreateBathCategoryPayload>({
    mutationKey: bathCategoryMutationKeys.create,
    mutationFn: (payload) => ritualBathRepository.createBathCategory(payload),

    onSettled: () => {
      return qc.invalidateQueries({
        queryKey: ritualBathQueryKeys.categories.all,
      });
    },

    retry: 0,
  });
}

// ============================================================================
// Update Bath Category Mutation
// ============================================================================

interface UpdateBathCategoryVariables {
  id: string;
  payload: Partial<UpdateBathCategoryPayload>;
}

interface UpdateBathCategoryContext {
  previous?: BathCategory | null;
}

/**
 * Mutation hook to update an existing bath category
 */
export function useUpdateBathCategoryMutation() {
  const qc = useQueryClient();

  return useMutation<
    BathCategory,
    Error,
    UpdateBathCategoryVariables,
    UpdateBathCategoryContext
  >({
    mutationKey: bathCategoryMutationKeys.update(),
    mutationFn: ({ id, payload }) =>
      ritualBathRepository.updateBathCategory(id, payload),

    onMutate: async ({ id, payload }) => {
      const key = ritualBathQueryKeys.categories.detail(id);
      await qc.cancelQueries({ queryKey: key });

      const previous = qc.getQueryData<BathCategory>(key) ?? null;

      qc.setQueryData<BathCategory | null>(key, (old) =>
        old ? { ...old, ...payload } : null
      );

      return { previous };
    },

    onError: (_error, { id }, context) => {
      if (context?.previous) {
        qc.setQueryData(
          ritualBathQueryKeys.categories.detail(id),
          context.previous
        );
      }
    },

    onSuccess: (data, { id }) => {
      qc.setQueryData(ritualBathQueryKeys.categories.detail(id), data);
    },

    onSettled: (_data, _error, { id }) => {
      return Promise.all([
        qc.invalidateQueries({
          queryKey: ritualBathQueryKeys.categories.detail(id),
        }),
        qc.invalidateQueries({
          queryKey: ritualBathQueryKeys.categories.all,
        }),
      ]);
    },

    retry: 0,
  });
}

// ============================================================================
// Delete Bath Category Mutation
// ============================================================================

interface DeleteBathCategoryVariables {
  id: string;
}

/**
 * Mutation hook to delete a bath category
 */
export function useDeleteBathCategoryMutation() {
  const qc = useQueryClient();

  return useMutation<void, Error, DeleteBathCategoryVariables>({
    mutationKey: bathCategoryMutationKeys.delete(),
    mutationFn: ({ id }) => ritualBathRepository.deleteBathCategory(id),

    onMutate: async ({ id }) => {
      await qc.cancelQueries({
        queryKey: ritualBathQueryKeys.categories.detail(id),
      });
      qc.removeQueries({ queryKey: ritualBathQueryKeys.categories.detail(id) });
    },

    onSettled: () => {
      return qc.invalidateQueries({
        queryKey: ritualBathQueryKeys.categories.all,
      });
    },

    retry: 0,
  });
}
